import { useFeatureGate } from "@statsig/react-bindings";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { Box } from "@/Components/v2/atoms/Box";
import { CircularProgress } from "@/Components/v2/atoms/Progress";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useAnalytics } from "@/hooks/useAnalytics";

import { useUpdateScenarioV3 } from "../../api/updateScenario";
import { DefaultBrandValue } from "../../utils";
import { ScenarioType, ScenarioTypeEnum, StatusEnum } from "../scenario-form/types";
import { ScenarioResultsHeader } from "./components/ScenarioResultsHeader";
import { useScenarioResultsContext } from "./ScenarioResultsProvider";
import { isNil, round } from "lodash";

interface ScenarioResultsLayoutProps {
  children: React.ReactNode;
  selectedGeography?: string | null;
  onGeographyChange?: (geography: string | null) => void;
  availableGeographies?: string[];
}

export const ScenarioResultsLayout = ({ children }: ScenarioResultsLayoutProps) => {
  const { uuid: scenarioId } = useParams<{ uuid: string }>();
  const { value: isScenariosApiEnabled } = useFeatureGate(featureFlag.scenarioBuilderApi);

  const {
    scenario,
    selectedGeography,
    isScenarioLoading,
    availableGeographies,
    setSelectedGeography,
  } = useScenarioResultsContext();

  const { scenarioStatusUpdated } = useAnalytics();

  const { mutate: updateScenario } = useUpdateScenarioV3({
    config: {
      onError: () => {
        toast.error(`Failed to update scenario. Please try again.`);
      },
    },
    isScenariosApiEnabled,
  });

  if (isScenarioLoading) {
    return <CircularProgress size={150} sx={{ marginTop: "30rem" }} />;
  }

  if (!scenario) {
    return <div>Scenario not found</div>;
  }

  const handleUpdateScenario = (updatedScenarioStatus: StatusEnum) => {
    if (scenarioId && scenario) {
      // Track status update before making the API call
      if (scenario.scenarioType && scenario.scenarioStatus) {
        scenarioStatusUpdated(
          ScenarioType.toString(
            scenario.scenarioType as ScenarioTypeEnum,
            scenario.scenarioCount || undefined
          ),
          scenarioId,
          scenario.name || "Untitled Scenario",
          scenario.scenarioStatus,
          updatedScenarioStatus
        );
      }

      updateScenario({
        scenarioUUID: scenarioId,
        scenario: { scenarioStatus: updatedScenarioStatus },
      });
    }
  };

  // Get target node for scope/business unit
  const brand = scenario.brand;
  const likelihoodPercentage = !isNil(scenario?.risk?.risk)
    ? round(scenario?.risk?.risk * 100, 2)
    : null;

  return (
    <Box sx={{ paddingX: 4, paddingY: 2 }}>
      <ScenarioResultsHeader
        scenarioId={scenarioId}
        scenarioName={scenario.name}
        scenarioDescription={scenario.description || ""}
        scenarioStatus={scenario.scenarioStatus as StatusEnum}
        brand={brand ?? DefaultBrandValue}
        startDate={scenario.startDate || new Date().toISOString()}
        endDate={scenario.endDate || new Date().toISOString()}
        scenarioType={scenario.scenarioType as ScenarioTypeEnum}
        geography={selectedGeography || undefined}
        availableGeographies={availableGeographies}
        onGeographyChange={setSelectedGeography}
        handleUpdateScenario={handleUpdateScenario}
        likelihoodPercentage={likelihoodPercentage}
      />

      <Box sx={{ marginTop: "0.5rem" }}>{children}</Box>
    </Box>
  );
};
